import * as THREE from 'three';
import { fromArrayBuffer } from 'geotiff';

import {
  dist,
  counterClockWise,
  getParentsPolygon,
  projectTo3DFromOrgVertices,
  pointInConcavePolygon,
  fitPlaneOnSegment,
  processSegmentsByLevelPriority,
  downloadCanvas
} from './geometry.js';

import { getBorders } from './3DReconstruction.js';

const DSM_NO_DATA_THRESHOLD = -9990;

const isValidDSMValue = (value) => Number.isFinite(value) && value > DSM_NO_DATA_THRESHOLD;

export const fillPolygon = (context, points, color, penSize = 0, fill = true) => {
  let [minx, miny, maxx, maxy] = [1e5, 1e5, 0, 0];

  if (points.length > 0) {
    if (fill) {
      context.fillStyle = color;
      let point = points[0];
      context.beginPath();
      context.moveTo(point[0], point[1]);
      for (let i = 1; i < points.length; i += 1) {
        point = points[i];
        context.lineTo(point[0], point[1]);
        minx = Math.min(minx, point[0]);
        miny = Math.min(miny, point[1]);
        maxx = Math.max(maxx, point[0]);
        maxy = Math.max(maxy, point[1]);
      }
      context.closePath();
      context.fill();
    }

    if (penSize) {
      context.strokeStyle = color;
      context.lineCap = 'round';
      context.lineWidth = penSize;
      for (let i = 1; i <= points.length; i += 1) {
        const point1 = points[i - 1];
        const point2 = points[i % points.length];
        context.beginPath();
        context.moveTo(point1[0], point1[1]);
        context.lineTo(point2[0], point2[1]);
        context.stroke();
      }
    }
  }

  return [minx, miny, maxx, maxy];
};

export const segmentIndexMask = (canvasPool, state, segs, height, width) => {
  const newCanvas = canvasPool.getCanvas(canvasPool.PoolKeys.SegmentIndexMask, width, height); // document.createElement('canvas');
  const context = canvasPool.getContext(canvasPool.PoolKeys.SegmentIndexMask, width, height); // newCanvas.getContext('2d');
  const scaleX = state?.rect?.width ? width / state.rect.width : 1;
  const scaleY = state?.rect?.height ? height / state.rect.height : 1;
  // newCanvas.width = width;
  // newCanvas.height = height;
  // context.drawImage(texture.image, 0, 0);
  // const ranges = [];
  context.clearRect(0, 0, newCanvas.width, newCanvas.height);
  const maskCanvas = canvasPool.CreateCanvas(width, height);
  const maskContext = maskCanvas.getContext('2d');
  const identityImage = context.createImageData(width, height);
  processSegmentsByLevelPriority(segs, state.parents, (seg, segIndex) => {
    const clr = segIndex + 1;
    const geometry = seg.geometry.map(([x, y]) => [x * scaleX, y * scaleY]);
    maskContext.clearRect(0, 0, width, height);
    fillPolygon(maskContext, geometry, 'rgb(255, 255, 255)');
    const xs = geometry.map(([x]) => x);
    const ys = geometry.map(([, y]) => y);
    const minX = Math.max(0, Math.floor(Math.min(...xs)));
    const minY = Math.max(0, Math.floor(Math.min(...ys)));
    const maxX = Math.min(width, Math.ceil(Math.max(...xs)));
    const maxY = Math.min(height, Math.ceil(Math.max(...ys)));
    const maskWidth = maxX - minX;
    const maskHeight = maxY - minY;
    if (maskWidth <= 0 || maskHeight <= 0) return;
    const coverage = maskContext.getImageData(minX, minY, maskWidth, maskHeight).data;
    for (let y = 0; y < maskHeight; y += 1) {
      for (let x = 0; x < maskWidth; x += 1) {
        const maskOffset = (y * maskWidth + x) * 4;
        if (coverage[maskOffset + 3] < 128) continue;
        const offset = ((minY + y) * width + minX + x) * 4;
        identityImage.data[offset] = clr;
        identityImage.data[offset + 1] = clr;
        identityImage.data[offset + 2] = clr;
        identityImage.data[offset + 3] = 255;
      }
    }
  });
  context.putImageData(identityImage, 0, 0);

  // const { orgVertices } = state;
  // const shadedIndices = [];
  // while (shadedIndices.length < segs.length) {
  //   segs.forEach((seg, segIndex) => {
  //     const segParents = getParentsPolygon(orgVertices, segIndex, true);
  //     if (segParents.every((p) => shadedIndices.includes(p))) {
  //       shadedIndices.push(segIndex);
  //       const clr = segIndex + 1;
  //       fillPolygon(context, seg.geometry, `rgb(${clr}, ${clr}, ${clr})`);
  //       // fillPolygon(context, seg.geometry, 'rgb(0,0,0)', 1, false);
  //       // ranges.push(segmentRanges);
  //     }
  //   });
  // }

  // downloadCanvas(newCanvas);
  return newCanvas;
};

const REFERENCE_RESOLUTION_CM = 7.5;

const scalePixelsForResolution = (value, resolution, minimum = 1) => {
  const currentResolution = Number(resolution);
  const scale =
    Number.isFinite(currentResolution) && currentResolution > 0
      ? currentResolution
      : REFERENCE_RESOLUTION_CM;
  return Math.max(minimum, Math.round((value * REFERENCE_RESOLUTION_CM) / scale));
};

export const getDisplacementBias = (
  context,
  points,
  width,
  height,
  resolution = REFERENCE_RESOLUTION_CM
) => {
  if (!points.length) return 0;

  let [minx, miny, maxx, maxy] = [1e5, 1e5, 0, 0];

  for (let i = 0; i < points.length; i += 1) {
    for (let j = 0; j < points[i].length; j += 1) {
      const point = points[i][j];
      minx = Math.min(minx, point[0]);
      miny = Math.min(miny, point[1]);
      maxx = Math.max(maxx, point[0]);
      maxy = Math.max(maxy, point[1]);
    }
  }

  const border = scalePixelsForResolution(75, resolution);
  const [xl, yb, xr, yt] = [
    Math.max(minx - border, 0),
    Math.max(miny - border, 0),
    Math.min(maxx + border, width),
    Math.min(maxy + border, height)
  ];
  const [w, h] = [Math.floor(xr - xl), Math.floor(yt - yb)];
  if (w <= 0 || h <= 0) return 0;

  const data = context.getImageData(xl, yb, w, h);
  const means = [0, 60, 120, 180, 240];
  const meansCount = [0, 0, 0, 0, 0];

  for (let cl = 0; cl < 3; cl += 1) {
    const newMeans = [0, 0, 0, 0, 0];
    const clsCount = [0, 0, 0, 0, 0];

    for (let j = 0; j < w; j += 1) {
      for (let i = 0; i < h; i += 1) {
        const index = (i * w + j) * 4;
        const intensity = data.data[index];
        if (intensity) {
          const d = means.map((m) => Math.abs(intensity - m));
          const sortedIndex = Array.from(Array(d.length).keys()).sort((a, b) => (d[a] > d[b] ? 1 : d[a] === d[b] ? 0 : -1));
          newMeans[sortedIndex[0]] += intensity;
          clsCount[sortedIndex[0]] += 1;
        }
      }
    }

    for (let i = 0; i < 5; i += 1) {
      if (clsCount[i] !== 0) means[i] = newMeans[i] / clsCount[i];
      meansCount[i] = clsCount[i];
    }
  }

  const t = (w * h) / 15;
  let [m, mc] = [0, 0];
  for (let i = 0; i < 5; i += 1) {
    if (meansCount[i] + mc < t) {
      m = (means[i] * meansCount[i] + m * mc) / (meansCount[i] + mc);
      mc += meansCount[i];
    } else {
      const result = (means[i] * meansCount[i] + m * mc) / (meansCount[i] + mc);
      return result;
    }
  }

  return 0;
};

export const setDisplacementMap = (
  segments,
  trees,
  displacementCanvas,
  context,
  dsmOffset = [0, 0],
  rgbWidth = null,
  rgbHeight = null,
  biasOverride = null,
  resolution = REFERENCE_RESOLUTION_CM
) => {
  const dsmWidth = displacementCanvas.width;
  const dsmHeight = displacementCanvas.height;
  const scaleX = rgbWidth && rgbHeight ? dsmWidth / rgbWidth : 1;
  const scaleY = rgbWidth && rgbHeight ? dsmHeight / rgbHeight : 1;

  const transformPoint = ([x, y]) => [x * scaleX, y * scaleY];

  const penSize = scalePixelsForResolution(30, resolution);
  const points = [];
  segments.forEach((seg) => {
    const transformed = (seg.geometry || []).map(transformPoint);
    points.push(transformed);
  });

  segments.forEach((seg) => {
    const transformed = (seg.geometry || []).map(transformPoint);
    fillPolygon(context, transformed, 'rgb(0, 0, 0)', penSize);
  });
  const bias = getDisplacementBias(
    context,
    points,
    displacementCanvas.width,
    displacementCanvas.height,
    resolution
  );
  const nextBias = Number.isFinite(Number(biasOverride)) && Number(biasOverride) >= 0 ? Number(biasOverride) : bias;
  const biasClamped = Math.max(0, Math.min(255, Math.round(nextBias)));
  const biasColor = `rgb(${biasClamped}, ${biasClamped}, ${biasClamped})`;
  segments.forEach((seg) => {
    const transformed = (seg.geometry || []).map(transformPoint);
    fillPolygon(context, transformed, biasColor, penSize);
  });

  trees.forEach((tree) => {
    if (tree.dsm && tree.dsm === 'Remove' && tree.geometry?.length >= 2) {
      const [c0, c1] = [transformPoint(tree.geometry[0]), transformPoint(tree.geometry[1])];
      const radius = ((c0[0] - c1[0]) ** 2 + (c0[1] - c1[1]) ** 2) ** 0.5;
      const center = c0;
      context.beginPath();
      context.arc(center[0], center[1], radius, 0, 2 * Math.PI, false);
      context.fillStyle = biasColor;
      context.fill();
      context.lineWidth = penSize;
      context.strokeStyle = biasColor;
      context.stroke();
    }
  });

  // downloadCanvas(displacementCanvas, 'editedDisplacementMap');
  return nextBias;
};

export const getOffset = (
  raster,
  rasterHeight,
  rasterWidth,
  borders,
  resolution,
  scale,
  center,
  rgbWidth = null,
  rgbHeight = null
) => {
  if (rasterWidth === 0 || rasterHeight === 0) return [0, 0];

  const scaleX = rgbWidth && rgbHeight ? rasterWidth / rgbWidth : 1;
  const scaleY = rgbWidth && rgbHeight ? rasterHeight / rgbHeight : 1;

  const range = Math.round((10 * 100) / (resolution / scale));
  const best = { score: 0, ox: 0, oy: 0 };
  for (let ox = -range; ox <= range; ox += 1) {
    for (let oy = -range; oy <= range; oy += 1) {
      const scores = borders.map(([, , , pntsPos, pntsNeg]) => {
        const heights = [pntsPos, pntsNeg].map((pnts) =>
          pnts
            .map((vp) => {
              const [iRgb, jRgb] = [vp.x / scale + center[0], vp.y / scale + center[1]];
              let [i, j] = [Math.round(iRgb * scaleX), Math.round(jRgb * scaleY)];
              i = Math.min(rasterWidth - 1, Math.max(0, i + ox));
              j = Math.min(rasterHeight - 1, Math.max(0, j + oy));
              const h = raster[i + j * rasterWidth];
              return isValidDSMValue(h) ? h : null;
            })
            .filter((h) => h !== null)
        );
        if (!heights[0].length || !heights[1].length) return 0;
        const [heightPos, heightNeg] = heights.map((hs) => hs.reduce((a, b) => a + b));
        return heightPos - heightNeg;
      });
      const score = scores.length ? scores.reduce((a, b) => a + b) : 0;
      if (score > best.score) {
        best.score = score;
        best.ox = ox;
        best.oy = oy;
      }
    }
  }
  const offset = [best.ox, best.oy];
  return offset;
};

export const getPointClouds = (
  canvasPool,
  state,
  center,
  ranges,
  resolution,
  scale,
  raster,
  rasterHeight,
  rasterWidth,
  minDSM,
  maxDSM,
  displacementBias,
  dsmOffset
) => {
  if (rasterWidth === 0 || rasterHeight === 0) return {};

  const rgbWidth = state?.rect?.width || rasterWidth;
  const rgbHeight = state?.rect?.height || rasterHeight;
  const scaleX = rasterWidth / rgbWidth;
  const scaleY = rasterHeight / rgbHeight;
  const segmentIndexCanvas = document.createElement('canvas');
  segmentIndexCanvas.width = rasterWidth;
  segmentIndexCanvas.height = rasterHeight;
  let context = segmentIndexCanvas.getContext('2d');
  context.imageSmoothingEnabled = false;
  const dsmBias = minDSM + (displacementBias / 255) * (maxDSM - minDSM);

  // const meanCount = 250;
  const result = {};
  const segmentRanges = {};
  state.segments.forEach((s, si) => {
    result[si] = [];
    const clr = si + 1;
    const g = s.geometry.map(([px, py]) => [
      Math.round(px * scaleX),
      Math.round(py * scaleY)
    ]);
    const gx = g.map(([px]) => px);
    const gy = g.map(([, py]) => py);
    const gminx = Math.min(...gx);
    const gminy = Math.min(...gy);
    const gmaxx = Math.max(...gx);
    const gmaxy = Math.max(...gy);
    segmentRanges[si] = [gminx, gminy, gmaxx, gmaxy];
    const gcount = s.geometry.length;
    fillPolygon(context, g, `rgb(${clr}, ${clr}, ${clr})`);
    s.lines.forEach((l, li) => {
      if (l === 'Eave' || l === 'Rake') {
        fillPolygon(
          context,
          [g[li], g[(li + 1) % gcount]],
          'rgb(0,0,0)',
          scalePixelsForResolution(
            12 * Math.sqrt(scaleX * scaleY),
            resolution
          ),
          false
        );
      }
    });
  });

  // downloadCanvas(segmentIndexCanvas, 'segmentIndices');
  const segData = context.getImageData(0, 0, rasterWidth, rasterHeight);

  const segCount = state.segments.length;
  const [minx, miny, maxx, maxy] = [
    Math.max(0, Math.floor(ranges[0] * scaleX)),
    Math.max(0, Math.floor(ranges[1] * scaleY)),
    Math.min(rasterWidth - 1, Math.ceil(ranges[2] * scaleX)),
    Math.min(rasterHeight - 1, Math.ceil(ranges[3] * scaleY))
  ];
  for (let i = minx; i <= maxx; i += 3) {
    for (let j = miny; j <= maxy; j += 3) {
      const index = j * (rasterWidth * 4) + i * 4;
      const sj = segData.data[index];
      if (sj > 0 && sj <= segCount) {
        const [gminx, gminy, gmaxx, gmaxy] = segmentRanges[sj - 1];
        if (i >= gminx && i <= gmaxx && j >= gminy && j <= gmaxy) {
          const io = Math.round(Math.min(rasterWidth - 1, Math.max(0, i + dsmOffset[0])));
          const jo = Math.round(Math.min(rasterHeight - 1, Math.max(0, j + dsmOffset[1])));
          const v = raster[io + jo * rasterWidth];
          if (!isValidDSMValue(v)) continue;
          const x = (i / scaleX - center[0]) * scale;
          const y = (j / scaleY - center[1]) * scale;
          const z = (v - dsmBias) * (100 / (resolution / scale));
          result[sj - 1].push(new THREE.Vector3(x, y, z));
        }
      }
    }
  }

  document.removeChild(segmentIndexCanvas);
  segmentIndexCanvas.height = 1;
  segmentIndexCanvas.width = 1;
  context = null;

  return result;
};

const extractDSMData = (
  canvasPool,
  rasters,
  rasterWidth,
  rasterLength,
  dsmOffset,
  segments,
  trees,
  rgbWidth = null,
  rgbHeight = null,
  biasOverride = null,
  resolution = REFERENCE_RESOLUTION_CM
) => {
  const canvasTiff = canvasPool.getCanvas(canvasPool.PoolKeys.GetTiff, rasterWidth, rasterLength);
  const contextTiff = canvasPool.getContext(canvasPool.PoolKeys.GetTiff, rasterWidth, rasterLength);
  const imgData = contextTiff.createImageData(rasterWidth, rasterLength);
  const missingPixelOffsets = [];

  let [minIntensity, maxIntensity] = [1e5, -1e5];

  for (let j = 0; j < rasterLength; j += 1) {
    for (let i = 0; i < rasterWidth; i += 1) {
      const intensity = rasters[0][i + j * rasterWidth];
      if (isValidDSMValue(intensity)) {
        minIntensity = Math.min(minIntensity, intensity);
        maxIntensity = Math.max(maxIntensity, intensity);
      }
    }
  }
  if (minIntensity === 1e5 || maxIntensity === -1e5) {
    minIntensity = 0;
    maxIntensity = 1;
  }

  const intensityRange = Math.max(maxIntensity - minIntensity, 1e-9);
  for (let j = 0; j < rasterLength; j += 1) {
    for (let i = 0; i < rasterWidth; i += 1) {
      const d = rasters[0][i + j * rasterWidth];
      const dataOffset = (i + j * rasterWidth) * 4;
      const hasDSM = isValidDSMValue(d);
      const intensity = hasDSM ? Math.round(((d - minIntensity) / intensityRange) * 255) : 0;
      if (!hasDSM) missingPixelOffsets.push(dataOffset);
      imgData.data[dataOffset] = intensity;
      imgData.data[dataOffset + 1] = intensity;
      imgData.data[dataOffset + 2] = intensity;
      imgData.data[dataOffset + 3] = 255;
    }
  }

  contextTiff.putImageData(imgData, 0, 0);

  const canvasOrgTiff = canvasPool.getCanvas(canvasPool.PoolKeys.GetTiffOrg, rasterWidth, rasterLength);
  const contextOrgTiff = canvasPool.getContext(canvasPool.PoolKeys.GetTiffOrg, rasterWidth, rasterLength);
  contextOrgTiff.drawImage(canvasTiff, 0, 0);
  const orgDSM = new THREE.CanvasTexture(canvasOrgTiff);
  orgDSM.needsUpdate = true;

  const canvasWithHouse = canvasPool.getCanvas(canvasPool.PoolKeys.GetTiffWithHouse, rasterWidth, rasterLength);
  const contextWithHouse = canvasPool.getContext(canvasPool.PoolKeys.GetTiffWithHouse, rasterWidth, rasterLength);
  contextWithHouse.drawImage(canvasTiff, -dsmOffset[0], -dsmOffset[1]);
  const dsmWithHouse = new THREE.CanvasTexture(canvasWithHouse);
  dsmWithHouse.needsUpdate = true;

  const canvasWithoutHouse = canvasPool.getCanvas(canvasPool.PoolKeys.GetTiffWithoutHouse, rasterWidth, rasterLength);
  const contextWithoutHouse = canvasPool.getContext(canvasPool.PoolKeys.GetTiffWithoutHouse, rasterWidth, rasterLength);
  contextWithoutHouse.drawImage(canvasTiff, -dsmOffset[0], -dsmOffset[1]);
  let bias = setDisplacementMap(
    segments,
    trees,
    canvasWithoutHouse,
    contextWithoutHouse,
    dsmOffset,
    rgbWidth,
    rgbHeight,
    biasOverride,
    resolution
  );
  if (missingPixelOffsets.length) {
    const biasClamped = Math.max(0, Math.min(255, Math.round(bias)));
    missingPixelOffsets.forEach((offset) => {
      imgData.data[offset] = biasClamped;
      imgData.data[offset + 1] = biasClamped;
      imgData.data[offset + 2] = biasClamped;
    });
    contextTiff.putImageData(imgData, 0, 0);
    contextOrgTiff.drawImage(canvasTiff, 0, 0);
    contextWithHouse.drawImage(canvasTiff, -dsmOffset[0], -dsmOffset[1]);
    contextWithoutHouse.drawImage(canvasTiff, -dsmOffset[0], -dsmOffset[1]);
    bias = setDisplacementMap(
      segments,
      trees,
      canvasWithoutHouse,
      contextWithoutHouse,
      dsmOffset,
      rgbWidth,
      rgbHeight,
      bias,
      resolution
    );
    orgDSM.needsUpdate = true;
    dsmWithHouse.needsUpdate = true;
  }
  const dsmWithoutHouse = new THREE.CanvasTexture(canvasWithoutHouse);
  dsmWithoutHouse.needsUpdate = true;

  const rasterData = [rasters[0], rasterLength, rasterWidth];
  const dsmTextureData = [orgDSM, dsmWithHouse, dsmWithoutHouse, minIntensity, maxIntensity, bias];
  const dsmParamsData = { bias, dsmOffset };

  const dsmData = {
    rasterData,
    dsmTextureData,
    dsmParamsData,
    rgbWidth,
    rgbHeight,
    resolution
  };

  return dsmData;
};

export const getTiff = (
  canvasPool,
  segments,
  trees,
  dsmSrc,
  dsmOffset,
  loadedHandler,
  errorHandler,
  rgbWidth = null,
  rgbHeight = null,
  biasOverride = null,
  resolution = REFERENCE_RESOLUTION_CM
) => {
  fetch(dsmSrc)
    .then((response) => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.arrayBuffer();
    })
    .then((arrayBuffer) => fromArrayBuffer(arrayBuffer))
    .then((tiff) => tiff.getImage())
    .then((image) => {
      return image.readRasters().then((rasters) => {
        const { ImageLength, ImageWidth } = image.fileDirectory;
        const dsmData = extractDSMData(
          canvasPool,
          rasters,
          ImageWidth,
          ImageLength,
          dsmOffset,
          segments,
          trees,
          rgbWidth,
          rgbHeight,
          biasOverride,
          resolution
        );
        loadedHandler(dsmData);
      });
    })
    .catch((err) => {
      console.log(err);
      errorHandler();
    });
};

export const updateTextureData = (canvasPool, dsmData, segments, trees) => {
  const { dsmTextureData, dsmParamsData } = dsmData;
  const { dsmOffset, bias: biasOverride } = dsmParamsData;
  const [orgDSM, , , minIntensity, maxIntensity] = dsmTextureData;

  if (!orgDSM?.image) return dsmTextureData;

  const { width, height } = orgDSM.image;

  const canvasWithHouse = canvasPool.getCanvas(canvasPool.PoolKeys.GetTiffWithHouse, width, height);
  const contextWithHouse = canvasPool.getContext(canvasPool.PoolKeys.GetTiffWithHouse, width, height);
  contextWithHouse.drawImage(orgDSM.image, -dsmOffset[0], -dsmOffset[1]);
  const dsmWithHouse = new THREE.CanvasTexture(canvasWithHouse);
  dsmWithHouse.needsUpdate = true;

  const canvasWithoutHouse = canvasPool.getCanvas(canvasPool.PoolKeys.GetTiffWithoutHouse, width, height);
  const contextWithoutHouse = canvasPool.getContext(canvasPool.PoolKeys.GetTiffWithoutHouse, width, height);
  contextWithoutHouse.drawImage(orgDSM.image, -dsmOffset[0], -dsmOffset[1]);
  const bias = setDisplacementMap(
    segments,
    trees,
    canvasWithoutHouse,
    contextWithoutHouse,
    dsmOffset,
    dsmData.rgbWidth ?? null,
    dsmData.rgbHeight ?? null,
    biasOverride,
    dsmData.resolution ?? REFERENCE_RESOLUTION_CM
  );
  const dsmWithoutHouse = new THREE.CanvasTexture(canvasWithoutHouse);
  dsmWithoutHouse.needsUpdate = true;

  const newTextureData = [orgDSM, dsmWithHouse, dsmWithoutHouse, minIntensity, maxIntensity, bias];
  return newTextureData;
};

export const setDSM = (state, raster, rasterHeight, rasterWidth, minDSM, maxDSM, displacementBias, dsmOffset) => {
  state.dsm = {
    raster,
    rasterHeight,
    rasterWidth,
    minDSM,
    maxDSM,
    displacementBias,
    dsmOffset
  };
};

export const measureByDSM = (canvasPool, state, segments, trees, dsmData) => {
  const { orgVertices, verticesType, resolution, scale, center } = state;
  const { rasterData, dsmTextureData } = dsmData;
  const [raster, rasterHeight, rasterWidth] = rasterData;
  const [, , , minDSM, maxDSM, displacementBias] = dsmTextureData;
  const roofVertices = orgVertices.filter((p, pi) => verticesType[pi] === 'roof');
  const borders = getBorders(state, roofVertices, true);
  const offset = getOffset(
    raster,
    rasterHeight,
    rasterWidth,
    borders,
    resolution,
    scale,
    center,
    state?.rect?.width ?? null,
    state?.rect?.height ?? null
  );
  const indicesMask = segmentIndexMask(canvasPool, state, segments, rasterHeight, rasterWidth);
  const baseDSM = minDSM + (displacementBias / 255) * (maxDSM - minDSM);
  fitPlaneOnSegment(segments, trees, state, center, scale, resolution, raster, baseDSM, offset, indicesMask);
  return offset;
};

export const getDSMData = (canvasPool, state, raster, segments, trees) => {
  const { orgVertices, verticesType, resolution, scale, center } = state;
  const roofVertices = orgVertices.filter((p, pi) => verticesType[pi] === 'roof');
  const borders = getBorders(state, roofVertices, true);
  const dsmOffset = getOffset(
    raster[0],
    raster.height,
    raster.width,
    borders,
    resolution,
    scale,
    center,
    state?.rect?.width ?? null,
    state?.rect?.height ?? null
  );
  const dsmData = extractDSMData(canvasPool, raster, raster.width, raster.height, dsmOffset, segments, trees);
  const { dsmTextureData } = dsmData;
  const [orgDSMTexture, , dsmTexture, minDSM, maxDSM, displacementBias] = dsmTextureData;
  return {
    dsmTexture,
    orgDSMTexture,
    minDSM,
    maxDSM,
    displacementBias,
    dsmOffset
  };
};
